import { PATH_NAME } from '@/configs/pathName';
import { USER_PLAN } from '@/constant/storage';
import {
  getFirst150Characters,
  removeBreakLinesAndSpaces,
} from '@/helpers/stringHelpers';
import { useSEO } from '@/hooks/useSEO';
import { IAuthResponse } from '@/interfaces/auth.interface';
import { postExternalLogin } from 'apis/tms/auth.api';
import { setAuthCookies, setUserInfoCookies } from 'common/authFunction';
import cookieCommons from 'common/cookieCommons';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { getProfile, getUidAndSlug } from '../../../apis/profile.api';
import { checkNullish } from '../../../helpers/checkNullish';
import * as ProfileInterface from '../../../interfaces/profile.interface';
import { NextPageWithLayout } from '../../page';

const EnterpriseProfileMode = dynamic(
  () =>
    import('../../../components/elements/profile/mode/EnterpriseProfileMode')
);
const BusinessProfileMode = dynamic(
  () => import('../../../components/elements/profile/mode/BusinessProfileMode')
);
const PersonalProfileMode = dynamic(
  () => import('../../../components/elements/profile/mode/PersonalProfileMode')
);
const CampaignTrackingMetrics = dynamic(
  () => import('@/components/script/CampaignTrackingMetrics'),
  {
    ssr: false, //to use localStorage
  }
);
const RemoveTokenInUrl = dynamic(
  () => import('@/components/script/RemoveTokenInUrl'),
  {
    ssr: false, //to use localStorage
  }
);

// components

//---------------------------------------------COMPONENT-----------------------------------//

const UserProfile: NextPageWithLayout<ProfileInterface.IUserProfileProps> = ({
  initData,
  slug,
  authData,
}: ProfileInterface.IUserProfileProps) => {
  const userPlan = initData?.profiles?.userPlan;

  if (authData) {
    setAuthCookies(authData);
    setUserInfoCookies(authData);
  }

  const hashProfileModal = {
    [USER_PLAN.Enterprise]: (
      <EnterpriseProfileMode
        structure={initData.profiles.enterprise}
        initData={initData}
      />
    ),
    [USER_PLAN.Business]: <BusinessProfileMode initData={initData} />,
    [USER_PLAN.Education]: (
      <PersonalProfileMode initData={initData} slug={slug} />
    ),
    [USER_PLAN.Personal]: (
      <PersonalProfileMode initData={initData} slug={slug} />
    ),
    [USER_PLAN.Verified]: (
      <PersonalProfileMode initData={initData} slug={slug} />
    ),
  };

  return (
    <>
      <RemoveTokenInUrl />
      <CampaignTrackingMetrics />
      {hashProfileModal[userPlan]}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  cookieCommons.setContext(context);
  const uid = context.params?.uid;
  const thuocsiToken = checkNullish(context.query?.token);
  const campaignRef = checkNullish(context.query?.ref);

  try {
    const res = await getUidAndSlug({
      uid_slug: typeof uid === 'string' ? uid : '',
    });

    if (uid === res.data.ownerId.toString() && res.data.slug) {
      const destinationUrl: string = `/${res.data.slug}`;
      const addQueryString = [
        campaignRef ? `ref=${campaignRef}` : undefined,
        thuocsiToken ? `token=${thuocsiToken}` : undefined,
      ]
        .filter((item) => !!item)
        .join('&');

      return {
        redirect: {
          destination:
            destinationUrl + (addQueryString ? '?' + addQueryString : ''),
          permanent: false,
        },
      };
    }

    let authData: IAuthResponse | undefined = undefined;

    if (thuocsiToken) {
      try {
        authData = await postExternalLogin({
          token: thuocsiToken,
        });
      } catch (error) {
        console.log(error);
      }
    }

    const req: ProfileInterface.IGetProfile = {
      userId: res.data.ownerId || '',
      accessToken:
        authData?.token || cookieCommons.getAccessToken() || 'undefined',
      requesterUserId:
        authData?.qBankUserId.toString() ||
        cookieCommons.getQbankUserId() ||
        'null',
    };

    const profile = await getProfile(req);
    const data = profile.data.data;

    const seo = JSON.parse(
      JSON.stringify(
        useSEO(data.profiles.qbankData.fullName, {
          description: getFirst150Characters(
            removeBreakLinesAndSpaces(data.profiles.bio)
          ),
          image: data.profiles.cover,
        })
      )
    );

    return {
      props: {
        initData: data,
        slug: res.data.slug ? res.data.slug : res.data.ownerId,
        authData: authData || '',
        seo,
      },
    };
  } catch (err) {
    return {
      redirect: {
        destination: PATH_NAME.NOT_FOUND,
        permanent: false,
      },
    };
  }
};

export default UserProfile;
